import { ApiResponse, PaginatedApiResponse } from '@/types/api.types';
import baseApi from './RootBaseApi';
import {
  IMemberData,
  MemberQueryParams,
  MembershipRequest,
  PaginatedMembershipResponse,
} from '@/types/club-management.types';
import { UserResponse } from '@/types/user-management.types';

export const ClubService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllMembershipRequests: builder.query<
      PaginatedMembershipResponse,
      {
        page?: number;
        pageSize?: number;
        pageLimit?: number;
        status?: number;
        search?: string;
        club_id?: string;
      }
    >({
      query: (params) => ({
        url: 'club-memberships/requests',
        method: 'GET',

        params: {
          page: params.page,
          limit: params.pageLimit ?? params.pageSize,
          status: params.status,
          search: params.search,
          club_id: params.club_id,
        },
      }),
      transformResponse: (response: PaginatedMembershipResponse) => response,
      providesTags: ['MembershipRequests', 'ClubsMembers'],
    }),

    reviewMembershipRequest: builder.mutation<
      ApiResponse<MembershipRequest>,
      {
        id: string;
        review: { status: string; reason?: string };
      }
    >({
      query: ({ id, review }) => ({
        url: `club-memberships/requests/${id}/review`,
        method: 'PATCH',
        data: review,
      }),
      transformResponse: (response: ApiResponse<MembershipRequest>) => response,
    }),

    getMembershipRequestById: builder.query<
      ApiResponse<MembershipRequest>,
      string
    >({
      query: (id) => ({
        url: `club-memberships/requests/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<MembershipRequest>) => response,
    }),

    getAllMembers: builder.query<
      PaginatedApiResponse<IMemberData>,
      MemberQueryParams
    >({
      query: (params) => ({
        url: 'club-memberships/members',
        method: 'GET',
        params,
      }),
      providesTags: ['ClubsMembers'],
    }),

    removeMember: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `club-memberships/members/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiResponse<void>) => response,
    }),

    // Get users by club ID for shift assignments
    getUsersByClubId: builder.query<
      PaginatedApiResponse<UserResponse>,
      {
        club_id: string;
        page?: number;
        limit?: number;
        search?: string;
        status?: number;
      }
    >({
      query: (params) => ({
        url: 'user-management',
        method: 'GET',
        params: {
          club_id: params.club_id,
          page: params.page,
          limit: params.limit,
          search: params.search,
          status: params.status,
        },
      }),
      providesTags: ['UserManagement'],
    }),

    // Update member role
    updateMemberRole: builder.mutation<
      ApiResponse<void>,
      {
        member_id: string;
        role: string;
      }
    >({
      query: ({ member_id, role }) => ({
        url: `club-memberships/members/${member_id}/role`,
        method: 'PATCH',
        data: { role },
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ['ClubsMembers'],
    }),
  }),
});

export const {
  useGetAllMembershipRequestsQuery,
  useGetMembershipRequestByIdQuery,
  useReviewMembershipRequestMutation,
  useGetAllMembersQuery,
  useRemoveMemberMutation,
  useGetUsersByClubIdQuery,
  useUpdateMemberRoleMutation,
} = ClubService;
